// src/pages/dashboard/IntegrationsSettings.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, AlertCircle, CheckCircle, X } from 'lucide-react';
import { auth, db } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, updateDoc, deleteField } from 'firebase/firestore';
import { socialPlatforms } from '../../data/socialPlatforms';
import {
  getStripeStatus,
  getZoomStatus,
  getGoogleCalendarStatus,
  getConvertKitStatus,
  getGoogleBusinessStatus,
  getSocialStatus,
  supportsOAuthVerification,
} from '../../utils/integrationStatus';
import { initiateSocialAuth, OAUTH_PLATFORMS } from '../../services/social-auth-service';
import LoadingButton from '../../components/common/LoadingButton';

const DF_GOLD = '#bb9749';

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

const SkeletonRow = () => (
  <div className="border border-gray-200 rounded-xl p-4 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
    <div className="h-3 bg-gray-100 rounded w-3/4"></div>
  </div>
);

// Brand mark icons — simple letter-marks, intentionally not pixel-perfect logos.
const STRIPE_ICON = (
  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#635BFF' }}>
    <span className="text-white font-bold text-lg leading-none">S</span>
  </div>
);

const GOOGLE_CALENDAR_ICON = (
  <div className="w-9 h-9 rounded-lg overflow-hidden border border-gray-200 flex flex-col flex-shrink-0">
    <div className="h-1.5 flex">
      <div className="flex-1" style={{ backgroundColor: '#4285F4' }} />
      <div className="flex-1" style={{ backgroundColor: '#EA4335' }} />
      <div className="flex-1" style={{ backgroundColor: '#FBBC05' }} />
      <div className="flex-1" style={{ backgroundColor: '#34A853' }} />
    </div>
    <div className="flex-1 flex items-center justify-center bg-white">
      <span className="text-[10px] font-bold text-gray-700 leading-none">Cal</span>
    </div>
  </div>
);

// Reuses the Zoom SVG from AvailabilitySettings.js:704–708.
const ZOOM_ICON = (
  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
    <svg viewBox="0 0 40 40" className="w-9 h-9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="8" fill="#2D8CFF" />
      <path d="M23 16v8L31 20l-8-4z" fill="white" />
      <rect x="9" y="13" width="14" height="14" rx="2.5" fill="white" />
    </svg>
  </div>
);

const CONVERTKIT_ICON = (
  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FB6970' }}>
    <span className="text-white font-bold text-xs leading-none">CK</span>
  </div>
);

const GOOGLE_BUSINESS_ICON = (
  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border border-gray-200 bg-white">
    <span className="font-bold text-base leading-none" style={{ color: '#4285F4' }}>G</span>
  </div>
);

// Visual treatment per status state.
const STATE_STYLES = {
  required: {
    badgeLabel: 'Required',
    badgeClass: 'bg-red-100 text-red-700 border-red-200',
    buttonClass: 'text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity',
    buttonStyle: { backgroundColor: DF_GOLD },
    buttonLabel: 'Connect',
    isTextStyle: false,
    accent: '',
  },
  recommended: {
    badgeLabel: 'Recommended',
    badgeClass: 'bg-purple-100 text-purple-700 border-purple-200',
    buttonClass: 'text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity',
    buttonStyle: { backgroundColor: DF_GOLD },
    buttonLabel: 'Connect',
    isTextStyle: false,
    accent: '',
  },
  connected: {
    badgeLabel: 'Connected',
    badgeClass: 'bg-green-100 text-green-700 border-green-200',
    buttonClass: 'text-sm font-medium inline-flex items-center gap-1 hover:opacity-80 transition-opacity',
    buttonStyle: { color: DF_GOLD },
    buttonLabel: 'Manage',
    isTextStyle: true,
    accent: '',
  },
  action_needed: {
    badgeLabel: 'Action needed',
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
    buttonClass: 'text-white bg-amber-600 hover:bg-amber-700 px-4 py-2 rounded-lg text-sm font-medium',
    buttonStyle: null,
    buttonLabel: 'Resolve',
    isTextStyle: false,
    accent: '',
  },
  not_connected: {
    badgeLabel: 'Not connected',
    badgeClass: 'bg-gray-100 text-gray-600 border-gray-200',
    buttonClass: 'text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 px-4 py-2 rounded-lg text-sm font-medium',
    buttonStyle: null,
    buttonLabel: 'Connect',
    isTextStyle: false,
    accent: '',
  },
};

const NON_SOCIAL_INTEGRATIONS = [
  {
    id: 'stripe',
    category: 'Payments',
    name: 'Stripe',
    description: 'Required to accept payments and receive payouts.',
    icon: STRIPE_ICON,
    route: '/dashboard/settings/business#stripe-connect',
    statusFn: getStripeStatus,
    requiredButtonLabel: 'Connect Stripe',
  },
  {
    id: 'google-calendar',
    category: 'Calendar & Video',
    name: 'Google Calendar',
    description: 'Sync busy times to your calendar and auto-create events for bookings.',
    icon: GOOGLE_CALENDAR_ICON,
    route: '/dashboard/settings/availability#google-calendar-integration',
    statusFn: getGoogleCalendarStatus,
  },
  {
    id: 'zoom',
    category: 'Calendar & Video',
    name: 'Zoom',
    description: 'Auto-create meeting links for bookings.',
    icon: ZOOM_ICON,
    route: '/dashboard/settings/availability#zoom-integration',
    statusFn: getZoomStatus,
  },
  {
    id: 'convertkit',
    category: 'Marketing',
    name: 'ConvertKit',
    description: 'Sync subscribers and run automated email campaigns.',
    icon: CONVERTKIT_ICON,
    route: '/dashboard/marketing#convertkit-integration',
    statusFn: getConvertKitStatus,
  },
  {
    id: 'google-business',
    category: 'Reviews',
    name: 'Google Business',
    description: 'Import your Google reviews to your DF profile.',
    icon: GOOGLE_BUSINESS_ICON,
    route: '/dashboard/reviews#google-business',
    statusFn: getGoogleBusinessStatus,
  },
];

// Spec order: Facebook, Instagram, LinkedIn, Pinterest, TikTok, X, YouTube.
// X's internal data ID is 'twitter' (per src/data/socialPlatforms.js).
const SOCIAL_PLATFORM_IDS_IN_ORDER = ['facebook', 'instagram', 'linkedin', 'pinterest', 'tiktok', 'twitter', 'youtube'];

const SOCIAL_INTEGRATIONS = SOCIAL_PLATFORM_IDS_IN_ORDER.map((id) => {
  const platformDef = socialPlatforms.find((p) => p.id === id);
  const isOAuth = supportsOAuthVerification(id);
  return {
    id: `social-${id}`,
    category: 'Social',
    name: id === 'twitter' ? 'X' : platformDef.name,
    description: isOAuth
      ? 'Verify your account to display a Verified badge on your profile.'
      : 'Add your profile URL to display on your public site.',
    icon: (
      <span className="w-9 h-9 flex items-center justify-center flex-shrink-0" style={{ color: platformDef.color }}>
        {platformDef.icon}
      </span>
    ),
    route: '/dashboard/settings/profile#social-accounts',
    statusFn: (p) => getSocialStatus(p, id),
    socialPlatform: id,
  };
});

const ALL_INTEGRATIONS = [...NON_SOCIAL_INTEGRATIONS, ...SOCIAL_INTEGRATIONS];

const INTEGRATIONS_BY_CATEGORY = ALL_INTEGRATIONS.reduce((acc, i) => {
  (acc[i.category] = acc[i.category] || []).push(i);
  return acc;
}, {});

const ActionButton = ({ isTextStyle, onClick, ariaLabel, className, style, children }) => {
  if (isTextStyle) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={ariaLabel}
        className={className}
        style={{ cursor: 'pointer', userSelect: 'none', ...style }}
      >
        {children}
      </span>
    );
  }
  return (
    <button type="button" onClick={onClick} aria-label={ariaLabel} className={className} style={style}>
      {children}
    </button>
  );
};

const IntegrationRow = ({ integration, practitioner, onClick, actionLabel, actionLoading }) => {
  const navigate = useNavigate();
  const status = integration.statusFn(practitioner || {});
  const styles = STATE_STYLES[status.state] || STATE_STYLES.not_connected;
  const defaultLabel = status.state === 'required'
    ? (integration.requiredButtonLabel || `Connect ${integration.name}`)
    : styles.buttonLabel;
  const buttonLabel = actionLabel || defaultLabel;
  // Avoid "Connect Stripe Stripe" — when requiredButtonLabel already contains the name,
  // use the label alone. Otherwise append the integration name for screen-reader context.
  const buttonAriaLabel = integration.requiredButtonLabel && status.state === 'required'
    ? buttonLabel
    : `${buttonLabel} ${integration.name}`;
  const showVerifiedBadge = integration.socialPlatform && status.verified === true;
  const showChevron = status.state === 'connected' && !onClick;
  const handleClick = onClick || (() => navigate(integration.route));

  // When the row's action is in flight (e.g., OAuth handoff is mid-redirect) AND the
  // button is a filled CTA (not a text-style link), render LoadingButton for the spinner.
  // Disconnect/Edit links stay on ActionButton — they're instant Firestore writes.
  const useLoadingButton = actionLoading && !styles.isTextStyle;

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-gray-200 rounded-xl bg-white ${styles.accent}`}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {integration.icon}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900">{integration.name}</h3>
            {showVerifiedBadge && (
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium" aria-label="OAuth verified">
                Verified
              </span>
            )}
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium border ${styles.badgeClass}`}
              aria-label={`${styles.badgeLabel} — ${integration.name}`}
            >
              {styles.badgeLabel}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{integration.description}</p>
        </div>
      </div>
      {useLoadingButton ? (
        <LoadingButton
          loading={true}
          loadingText="Connecting…"
          className={`${styles.buttonClass} flex-shrink-0 self-start sm:self-auto`}
          style={styles.buttonStyle}
          ariaLabel={buttonAriaLabel}
        >
          {buttonLabel}
        </LoadingButton>
      ) : (
        <ActionButton
          isTextStyle={styles.isTextStyle}
          onClick={handleClick}
          className={`${styles.buttonClass} flex-shrink-0 self-start sm:self-auto`}
          style={styles.buttonStyle}
          ariaLabel={buttonAriaLabel}
        >
          {buttonLabel}
          {showChevron && <ChevronRight className="w-4 h-4" />}
        </ActionButton>
      )}
    </div>
  );
};

const CategorySection = ({ title, description, integrations, practitioner, loading, onClickMap, actionLabelMap, actionLoadingMap }) => (
  <section>
    <h2 className="text-sm font-semibold text-gray-700 mb-1">{title}</h2>
    {description && <p className="text-xs text-gray-500 mb-3">{description}</p>}
    <div className="space-y-2">
      {loading
        ? Array.from({ length: integrations.length }).map((_, i) => <SkeletonRow key={i} />)
        : integrations.length === 0
          ? <p className="text-xs text-gray-400 italic px-1">No integrations in this category yet.</p>
          : integrations.map((integration) => (
              <IntegrationRow
                key={integration.id}
                integration={integration}
                practitioner={practitioner}
                onClick={onClickMap?.[integration.id]}
                actionLabel={actionLabelMap?.[integration.id]}
                actionLoading={actionLoadingMap?.[integration.id]}
              />
            ))}
    </div>
  </section>
);

const IntegrationsSettings = () => {
  const [userId, setUserId] = useState(null);
  const [practitioner, setPractitioner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [connectSuccess, setConnectSuccess] = useState(null);
  const [connectError, setConnectError] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualPlatform, setManualPlatform] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const [existingValue, setExistingValue] = useState('');
  // Per-platform OAuth loading state. Keyed by platformId (facebook|instagram|tiktok).
  // Set true before initiateSocialAuth(); rely on browser nav for success cleanup.
  // Only set false on init failure (catch handler) so the user can retry.
  const [oauthLoading, setOauthLoading] = useState({});

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setRetryKey((k) => k + 1);
  };

  // Read OAuth callback return params on mount.
  // Mirrors SocialLinksPanel.js:41-47 — when the Cloud Function redirect lands
  // here with ?social=facebook_connected (etc), surface a toast banner.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const socialParam = params.get('social');
    if (socialParam?.endsWith('_connected')) {
      setConnectSuccess(socialParam.replace('_connected', ''));
    } else if (socialParam?.endsWith('_error')) {
      setConnectError(socialParam.replace('_error', ''));
    }
    // Clean the URL immediately so the dirty `?social=` + Facebook's `#_=_` hash
    // artifact don't interfere with subsequent renders / event handlers.
    // Issue 1: clicking Disconnect on the OAuth callback URL was a no-op until
    // the URL was manually cleaned.
    if (socialParam) {
      const url = new URL(window.location.href);
      url.searchParams.delete('social');
      const cleanHash = url.hash === '#_=_' ? '' : url.hash;
      window.history.replaceState({}, '', url.pathname + (url.search || '') + cleanHash);
    }
  }, []);

  const dismissBanner = (setter) => {
    setter(null);
    const url = new URL(window.location.href);
    if (url.searchParams.has('social')) {
      url.searchParams.delete('social');
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    }
  };

  const handleOpenManual = (platform) => {
    setManualPlatform(platform);
    const published = practitioner?.website_published?.contactFooter?.socialLinks?.[platform.id];
    const draft = practitioner?.website_draft?.contactFooter?.socialLinks?.[platform.id];
    const initial = published || draft || '';
    setExistingValue(initial);
    setManualInput(initial);
    setShowManualModal(true);
  };

  const handleCloseManual = () => {
    setShowManualModal(false);
    setManualPlatform(null);
    setManualInput('');
    setExistingValue('');
  };

  // Risk 2 (per spec): write to BOTH website_draft and website_published so the
  // change appears on the public site without requiring a Site Editor publish.
  const handleSaveManual = async () => {
    if (!userId || !manualPlatform) return;
    const url = manualInput.trim();
    if (!url) return;
    try {
      await updateDoc(doc(db, 'practitioners', userId), {
        [`website_draft.contactFooter.socialLinks.${manualPlatform.id}`]: url,
        [`website_published.contactFooter.socialLinks.${manualPlatform.id}`]: url,
      });
      setPractitioner((prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        ['website_draft', 'website_published'].forEach((key) => {
          const website = { ...(updated[key] || {}) };
          const contactFooter = { ...(website.contactFooter || {}) };
          contactFooter.socialLinks = { ...(contactFooter.socialLinks || {}), [manualPlatform.id]: url };
          website.contactFooter = contactFooter;
          updated[key] = website;
        });
        return updated;
      });
      handleCloseManual();
    } catch (err) {
      console.error('Save manual URL error:', err);
    }
  };

  const handleRemoveManual = async (platformId) => {
    if (!userId) return false;
    const platform = socialPlatforms.find((p) => p.id === platformId);
    const platformName = platform?.name || platformId;
    if (!window.confirm(`Remove ${platformName} link?`)) return false;
    try {
      await updateDoc(doc(db, 'practitioners', userId), {
        [`website_draft.contactFooter.socialLinks.${platformId}`]: deleteField(),
        [`website_published.contactFooter.socialLinks.${platformId}`]: deleteField(),
      });
      setPractitioner((prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        ['website_draft', 'website_published'].forEach((key) => {
          if (!updated[key]?.contactFooter?.socialLinks) return;
          const website = { ...updated[key] };
          const contactFooter = { ...website.contactFooter };
          const socialLinks = { ...contactFooter.socialLinks };
          delete socialLinks[platformId];
          contactFooter.socialLinks = socialLinks;
          website.contactFooter = contactFooter;
          updated[key] = website;
        });
        return updated;
      });
      return true;
    } catch (err) {
      console.error('Remove manual URL error:', err);
      return false;
    }
  };

  const handleSocialOAuth = async (platformId) => {
    if (!userId) return;
    setOauthLoading((prev) => ({ ...prev, [platformId]: true }));
    try {
      await initiateSocialAuth(platformId, userId);
      // Browser navigates away to OAuth provider — leave loading=true so the button
      // stays in spinner state during the handoff. State resets on full page reload.
    } catch (err) {
      console.error('OAuth init error:', err);
      setConnectError(platformId);
      setOauthLoading((prev) => ({ ...prev, [platformId]: false }));
    }
  };

  const handleSocialDisconnect = async (platformId) => {
    if (!userId) return;
    const platform = socialPlatforms.find((p) => p.id === platformId);
    const platformName = platform?.name || platformId;
    if (!window.confirm(`Disconnect ${platformName}?`)) return;
    try {
      // Single atomic write: clear OAuth connection + manual URL (both draft + published)
      // so disconnect fully removes the platform from all UI surfaces.
      await updateDoc(doc(db, 'practitioners', userId), {
        [`socialConnections.${platformId}`]: deleteField(),
        [`website_draft.contactFooter.socialLinks.${platformId}`]: deleteField(),
        [`website_published.contactFooter.socialLinks.${platformId}`]: deleteField(),
      });
      setPractitioner((prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        if (updated.socialConnections) {
          const newConnections = { ...updated.socialConnections };
          delete newConnections[platformId];
          updated.socialConnections = newConnections;
        }
        ['website_draft', 'website_published'].forEach((key) => {
          if (!updated[key]?.contactFooter?.socialLinks) return;
          const website = { ...updated[key] };
          const contactFooter = { ...website.contactFooter };
          const socialLinks = { ...contactFooter.socialLinks };
          delete socialLinks[platformId];
          contactFooter.socialLinks = socialLinks;
          website.contactFooter = contactFooter;
          updated[key] = website;
        });
        return updated;
      });
    } catch (err) {
      console.error('Disconnect error:', err);
    }
  };

  // Phase 1: resolve uid — use auth.currentUser if already loaded (SPA navigation),
  // otherwise wait for onAuthStateChanged with a 3s safety timeout for the
  // unauthenticated case. Pattern mirrors Reviews.js:583 and Marketing.js:802.
  useEffect(() => {
    if (auth?.currentUser) {
      setUserId(auth.currentUser.uid);
      return;
    }
    let done = false;
    const timeout = setTimeout(() => {
      if (!done) {
        done = true;
        setLoading(false);
      }
    }, 3000);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (done) return;
      if (!user) return;
      done = true;
      clearTimeout(timeout);
      setUserId(user.uid);
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  // Phase 2: real-time listener on the practitioner doc.
  useEffect(() => {
    if (!userId || !db) return;
    setError(null);
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, 'practitioners', userId),
      (snap) => {
        setPractitioner(snap.exists() ? snap.data() : {});
        setLoading(false);
      },
      (err) => {
        console.error('IntegrationsSettings practitioner subscription error:', err);
        setError(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [userId, retryKey]);

  // TODO: Once Step 7 is verified working, remove the BRIDGE effects in
  // SocialLinksPanel.js (the two useEffects above the style handlers in that file).
  // They duplicate the two useEffects below and can be safely deleted.

  // Bridge: sync OAuth profileUrl into website_draft + website_published socialLinks.
  // Writes to BOTH paths so the public site updates without a Site Editor publish (Risk 2).
  useEffect(() => {
    if (!userId || !practitioner?.socialConnections) return;
    const socialConnections = practitioner.socialConnections;
    const currentLinks = practitioner?.website_published?.contactFooter?.socialLinks || {};
    const updates = {};
    OAUTH_PLATFORMS.forEach((platformId) => {
      const conn = socialConnections[platformId];
      if (conn?.connected && conn?.profileUrl && !currentLinks[platformId]) {
        updates[`website_draft.contactFooter.socialLinks.${platformId}`] = conn.profileUrl;
        updates[`website_published.contactFooter.socialLinks.${platformId}`] = conn.profileUrl;
      }
    });
    if (Object.keys(updates).length > 0) {
      updateDoc(doc(db, 'practitioners', userId), updates).catch((err) => {
        console.error('Sync socialLinks error:', err);
      });
    }
  }, [userId, practitioner?.socialConnections]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bridge: sync verified status into both website_draft and website_published.
  useEffect(() => {
    if (!userId || !practitioner?.socialConnections) return;
    const socialConnections = practitioner.socialConnections;
    const currentVerified = practitioner?.website_published?.contactFooter?.socialVerified || {};
    const updates = {};
    OAUTH_PLATFORMS.forEach((platformId) => {
      if (socialConnections[platformId]?.connected === true && !currentVerified[platformId]) {
        updates[`website_draft.contactFooter.socialVerified.${platformId}`] = true;
        updates[`website_published.contactFooter.socialVerified.${platformId}`] = true;
      }
    });
    if (Object.keys(updates).length > 0) {
      updateDoc(doc(db, 'practitioners', userId), updates).catch((err) => {
        console.error('Sync socialVerified error:', err);
      });
    }
  }, [userId, practitioner?.socialConnections]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-row click + label + loading overrides for the Social category.
  // CategorySection passes these through to IntegrationRow.
  const socialClickMap = {};
  const socialActionLabelMap = {};
  const socialLoadingMap = {};
  SOCIAL_INTEGRATIONS.forEach((social) => {
    const platformId = social.socialPlatform;
    socialLoadingMap[social.id] = !!oauthLoading[platformId];
    const status = social.statusFn(practitioner || {});
    const isOAuthSupported = OAUTH_PLATFORMS.includes(platformId);
    const isOAuthConnected = practitioner?.socialConnections?.[platformId]?.connected === true;
    const isManualConnected = !isOAuthConnected && status.state === 'connected';
    const platformDef = socialPlatforms.find((p) => p.id === platformId);

    socialClickMap[social.id] = () => {
      if (isOAuthConnected) {
        handleSocialDisconnect(platformId);
      } else if (isManualConnected) {
        handleOpenManual(platformDef);
      } else if (isOAuthSupported) {
        handleSocialOAuth(platformId);
      } else {
        handleOpenManual(platformDef);
      }
    };

    if (isOAuthConnected) {
      socialActionLabelMap[social.id] = 'Disconnect';
    } else if (isManualConnected) {
      socialActionLabelMap[social.id] = 'Edit';
    } else if (!isOAuthSupported) {
      socialActionLabelMap[social.id] = 'Add link';
    }
    // OAuth supported but not connected: no override → IntegrationRow uses default 'Connect'
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Settings
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Integrations</span>
        </nav>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Integrations</h1>
          <p className="text-gray-600">Connect external tools and services to power your DF practice.</p>
        </div>

        {connectSuccess && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg mb-6">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800 font-medium">
              {capitalize(connectSuccess)} connected!
            </p>
            <span
              role="button"
              tabIndex={0}
              onClick={() => dismissBanner(setConnectSuccess)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  dismissBanner(setConnectSuccess);
                }
              }}
              aria-label="Dismiss success message"
              className="ml-auto text-green-500 hover:text-green-700 hover:bg-green-100 active:scale-95 p-0.5 rounded transition-colors"
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <X className="w-4 h-4" />
            </span>
          </div>
        )}

        {connectError && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg mb-6">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">
              Couldn't connect {capitalize(connectError)}. Please try again.
            </p>
            <span
              role="button"
              tabIndex={0}
              onClick={() => dismissBanner(setConnectError)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  dismissBanner(setConnectError);
                }
              }}
              aria-label="Dismiss error message"
              className="ml-auto text-red-500 hover:text-red-700 hover:bg-red-100 active:scale-95 p-0.5 rounded transition-colors"
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <X className="w-4 h-4" />
            </span>
          </div>
        )}

        <div className="space-y-8">
          {error ? (
            <div className="bg-white border border-red-200 rounded-xl p-8 text-center" role="alert">
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-base font-semibold text-gray-900 mb-1">Couldn't load integrations</h2>
              <p className="text-sm text-gray-600 mb-4 max-w-sm mx-auto">
                We couldn't fetch your integration status. Check your connection and try again.
              </p>
              <button
                type="button"
                onClick={handleRetry}
                className="text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              <CategorySection title="Payments" description="Required for accepting payments and processing payouts" integrations={INTEGRATIONS_BY_CATEGORY.Payments || []} practitioner={practitioner} loading={loading} />
              <CategorySection title="Calendar & Video" description="Sync your calendar and auto-create video meeting links" integrations={INTEGRATIONS_BY_CATEGORY['Calendar & Video'] || []} practitioner={practitioner} loading={loading} />
              <CategorySection title="Marketing" description="Email marketing tools and automated subscriber sync" integrations={INTEGRATIONS_BY_CATEGORY.Marketing || []} practitioner={practitioner} loading={loading} />
              <CategorySection title="Reviews" description="Import reviews from external platforms" integrations={INTEGRATIONS_BY_CATEGORY.Reviews || []} practitioner={practitioner} loading={loading} />
              <CategorySection
                title="Social"
                description="Verify or link your social media accounts"
                integrations={INTEGRATIONS_BY_CATEGORY.Social || []}
                practitioner={practitioner}
                loading={loading}
                onClickMap={socialClickMap}
                actionLabelMap={socialActionLabelMap}
                actionLoadingMap={socialLoadingMap}
              />
            </>
          )}
        </div>
      </div>

      {showManualModal && manualPlatform && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  {manualPlatform.name} Link
                </h3>
                <p className="text-sm text-gray-600">
                  Enter your profile URL or username
                </p>
              </div>
              <span
                role="button"
                tabIndex={0}
                onClick={handleCloseManual}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleCloseManual();
                  }
                }}
                aria-label="Close modal"
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:scale-95 rounded transition-colors"
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <X className="w-5 h-5" />
              </span>
            </div>

            <div className="space-y-4">
              {manualPlatform.urlPrefix && (
                <div className="text-xs text-gray-500 font-mono">{manualPlatform.urlPrefix}</div>
              )}
              <input
                type="text"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder={manualPlatform.placeholder || `${manualPlatform.name} username`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                autoFocus
              />
              <div className="flex justify-between items-center">
                {existingValue ? (
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await handleRemoveManual(manualPlatform.id);
                      if (ok) handleCloseManual();
                    }}
                    className="text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors"
                  >
                    Remove link
                  </button>
                ) : <div />}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCloseManual}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveManual}
                    disabled={!manualInput.trim()}
                    className="px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-opacity hover:opacity-90"
                    style={{ backgroundColor: DF_GOLD }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsSettings;
